// frontend/src/pages/OrdenCompraForm.jsx
import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Stack,
  Typography,
  Paper,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material';
import { AddCircle, RemoveCircle } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchOrdenCompra, createOrdenCompra, updateOrdenCompra } from '../api/ordenes_compra';
import { fetchProveedores } from '../api/proveedores';
import { searchProductosLight } from '../api/productos';
import { useLoading } from '../contexts/LoadingContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import AsyncAutocomplete from '../components/AsyncAutocomplete';

export default function OrdenCompraForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const { start, stop } = useLoading();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const theme = useTheme();

  const [cabecera, setCabecera] = useState({
    codigo: '',
    proveedor_id: '',
    estado: 'pendiente',
  });
  const [proveedorSel, setProveedorSel] = useState(null);
  const [lineas, setLineas] = useState([]);
  const [dirty, setDirty] = useState(false);

  // =========================
  // ESCANEO GLOBAL (barcode)
  // =========================
  const scanBufferRef = useRef('');
  const scanFirstTsRef = useRef(0);
  const scanLastTsRef = useRef(0);
  const scanIntervalsRef = useRef([]); // deltas entre teclas

  const scanBusyRef = useRef(false);
  const scanTimerRef = useRef(null);

  // Para revertir lo que el scanner logró escribir en el input (ej: el primer "8")
  const scanTargetElRef = useRef(null);
  const scanTargetValueRef = useRef('');

  // Dedupe: evita procesar 2 veces el mismo código (timeout + Enter/Tab, o scanner duplicando)
  const lastProcessedRef = useRef({ code: '', ts: 0 });

  const buildLabel = (p) => {
    const nombre = String(p?.nombre ?? '').trim();
    const marca = String(p?.marca ?? '').trim();
    return marca ? `${nombre} — ${marca}` : nombre;
  };

  const addOrIncrementLineaByProducto = (prod) => {
    const pid = String(prod.id);

    setLineas((prev) => {
      const idx = prev.findIndex(
        (ln) => String(ln.productoObj?.id ?? ln.producto_id) === pid
      );

      // Si ya existe, suma 1 a cantidad
      if (idx >= 0) {
        const next = [...prev];
        const currentQty = Number(next[idx].cantidad) || 0;
        next[idx] = { ...next[idx], cantidad: String(currentQty + 1) };
        return next;
      }

      // Si no existe, crea una nueva línea
      return [
        ...prev,
        {
          producto_id: pid,
          productoObj: { id: pid, label: buildLabel(prod) },
          cantidad: '1',
          precio_unitario: '',
          impuesto: '0',
          descuento: '0',
        },
      ];
    });

    setDirty(true);
  };

  const processScanCode = async (codeRaw) => {
    const code = String(codeRaw || '').trim();
    if (!code) return;

    if (scanBusyRef.current) return;
    scanBusyRef.current = true;

    try {
      const res = await searchProductosLight({ q: code, page: 1, pageSize: 30 });
      const items = Array.isArray(res?.items) ? res.items : [];

      const found =
        items.find((it) => String(it.sku || '').trim() === code) || items[0];

      if (!found) {
        showToast({
          message: `No se encontró el producto para: ${code}`,
          severity: 'warning',
        });
        return;
      }

      addOrIncrementLineaByProducto(found);
    } catch (err) {
      console.error(err);
      showToast({ message: 'Error buscando producto por SKU.', severity: 'error' });
    } finally {
      scanBusyRef.current = false;
    }
  };

  const clearScanState = () => {
    scanBufferRef.current = '';
    scanFirstTsRef.current = 0;
    scanLastTsRef.current = 0;
    scanIntervalsRef.current = [];
    scanTargetElRef.current = null;
    scanTargetValueRef.current = '';

    if (scanTimerRef.current) {
      clearTimeout(scanTimerRef.current);
      scanTimerRef.current = null;
    }
  };

  const avg = (arr) => {
    if (!arr || !arr.length) return 999999;
    const s = arr.reduce((a, b) => a + b, 0);
    return s / arr.length;
  };

  const finalizeScan = (evt) => {
    const buf = String(scanBufferRef.current || '').trim();
    const first = Number(scanFirstTsRef.current || 0);
    const last = Number(scanLastTsRef.current || 0);
    const duration = first && last ? last - first : 999999;
    const len = buf.length;

    // Heurística final (más tolerante):
    // - largo mínimo
    // - duración total razonable
    // - promedio de intervalos relativamente rápido
    const avgDelta = avg(scanIntervalsRef.current);
    const looksLikeScan = len >= 4 && duration <= 3000 && avgDelta <= 200;

    // limpia timer ya
    if (scanTimerRef.current) {
      clearTimeout(scanTimerRef.current);
      scanTimerRef.current = null;
    }

    // si parece scan:
    if (looksLikeScan) {
      // dedupe: si llega repetido muy cerca, ignóralo
      const now = Date.now();
      const lastP = lastProcessedRef.current;
      if (lastP.code === buf && now - lastP.ts < 900) {
        clearScanState();
        return;
      }
      lastProcessedRef.current = { code: buf, ts: now };

      // evita submit/tab y cosas raras
      if (evt) {
        evt.preventDefault();
        evt.stopPropagation();
      }

      // restaura input si el scanner alcanzó a escribir algo
      const el = scanTargetElRef.current;
      const prevVal = scanTargetValueRef.current;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) {
        try {
          el.value = String(prevVal ?? '');
          el.dispatchEvent(new Event('input', { bubbles: true }));
        } catch (_) {}
      }

      // procesa SKU
      processScanCode(buf);
      clearScanState();
      return;
    }

    // si NO parece scan, solo resetea (era tipeo humano)
    clearScanState();
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (!e) return;
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      const key = typeof e.key === 'string' ? e.key : '';
      const now = Date.now();

      // Terminadores típicos del scanner: Enter / Tab
      if (key === 'Enter' || key === 'NumpadEnter' || key === 'Tab') {
        // finaliza (si era scan, aquí se procesa)
        finalizeScan(e);
        return;
      }

      // Solo caracteres imprimibles
      if (key.length !== 1) return;

      const last = Number(scanLastTsRef.current || 0);
      const delta = last ? now - last : 0;

      // Nuevo “paquete” si pasó mucho tiempo entre teclas
      if (!scanFirstTsRef.current || delta > 300) {
        scanFirstTsRef.current = now;
        scanBufferRef.current = key;
        scanIntervalsRef.current = [];

        // guarda input enfocado para restaurar si se cuela texto del scanner
        const active = document.activeElement;
        if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {
          scanTargetElRef.current = active;
          scanTargetValueRef.current = active.value ?? '';
        } else {
          scanTargetElRef.current = null;
          scanTargetValueRef.current = '';
        }
      } else {
        scanBufferRef.current = String(scanBufferRef.current || '') + key;
        scanIntervalsRef.current = [...scanIntervalsRef.current, delta];
      }

      scanLastTsRef.current = now;

      // Si ya huele a scanner (rápido + ya tenemos varias teclas), bloquea que se escriba en inputs
      // (esto hace que NO se te llene el campo "Código")
      const avgDeltaNow = avg(scanIntervalsRef.current);
      const likelyScannerNow = scanBufferRef.current.length >= 2 && avgDeltaNow <= 120;
      if (likelyScannerNow) {
        e.preventDefault();
        e.stopPropagation();
      }

      // Timeout: si el scanner NO manda Enter/Tab, cerramos tras 220ms sin teclas
      if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
      scanTimerRef.current = setTimeout(() => finalizeScan(null), 220);
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      clearScanState();
    };
  }, []); // mantiene tu lógica

  // =========================
  // CARGA DE ORDEN EXISTENTE
  // =========================
  useEffect(() => {
    stop();
    if (id) {
      fetchOrdenCompra(id)
        .then((r) => {
          const data = r?.data ?? r ?? {};

          setCabecera({
            codigo: data.codigo || '',
            proveedor_id: data.proveedor_id ? String(data.proveedor_id) : '',
            estado: data.estado || 'pendiente',
          });

          if (data.proveedor_nombre && data.proveedor_id) {
            setProveedorSel({
              id: String(data.proveedor_id),
              label: data.proveedor_nombre,
            });
          }

          const baseLineas = (data.lineas || []).map((ln) => ({
            producto_id: String(ln.producto?.id || ''),
            productoObj: ln.producto
              ? { id: String(ln.producto.id), label: ln.producto.nombre }
              : null,
            cantidad: ln.cantidad ?? '',
            precio_unitario: ln.precio_unitario ?? '',
            impuesto: ln.impuesto ?? '',
            descuento: ln.descuento ?? '',
          }));

          setLineas(baseLineas);
        })
        .catch(() =>
          showToast({ message: 'Error al cargar la orden.', severity: 'error' })
        );
    }
  }, [id, showToast, stop]);

  // ==========
  // FETCHERS
  // ==========
  const fetchPageProveedores = async ({ q, page, limit }) => {
    const res = await fetchProveedores({ page, limit, search: q });
    const payload = res?.data ?? {};
    const data = Array.isArray(payload.data) ? payload.data : [];
    const total = Number(payload.total || 0);
    return {
      items: data.map((p) => ({ id: String(p.id), label: p.nombre })),
      hasMore: page * limit < total,
    };
  };

  const fetchPageProductos = async ({ q, page, limit }) => {
    const res = await searchProductosLight({ q, page, pageSize: limit });
    const items = Array.isArray(res?.items) ? res.items : [];
    return {
      items: items.map((it) => ({
        id: String(it.id),
        label: `${it.nombre} — ${it.marca ?? ''}`,
        nombre: it.nombre,
        marca: it.marca ?? '',
        sku: it.sku ?? '',
        precio_venta: it.precio_venta,
      })),
      hasMore: Boolean(res?.hasMore),
    };
  };

  // ==========
  // HANDLERS
  // ==========
  const handleCabeceraChange = (e) => {
    setCabecera((c) => ({ ...c, [e.target.name]: e.target.value }));
    setDirty(true);
  };

  const handleLineaChange = (i, e) => {
    setLineas((l) =>
      l.map((ln, idx) =>
        idx === i ? { ...ln, [e.target.name]: e.target.value } : ln
      )
    );
    setDirty(true);
  };

  const addLinea = () => {
    setLineas((l) => [
      ...l,
      {
        producto_id: '',
        productoObj: null,
        cantidad: '',
        precio_unitario: '',
        impuesto: '',
        descuento: '',
      },
    ]);
    setDirty(true);
  };

  const removeLinea = (i) => {
    setLineas((l) => l.filter((_, idx) => idx !== i));
    setDirty(true);
  };

  // =========================
  // CALCULO SUBTOTAL
  // =========================
const calcularSubtotal = (ln) => {
  const c = Number(ln.cantidad) || 0;
  const pu = Number(ln.precio_unitario) || 0;
  const imp = Number(ln.impuesto) || 0;
  const d = Number(ln.descuento) || 0;

  const base = c * pu;
  const baseConDescuento = base * (1 - d / 100);
  const totalConImpuesto = baseConDescuento * (1 + imp / 100);

  return totalConImpuesto; // sin redondear aquí
};

 const totalOrden = useMemo(() => {
  const raw = lineas.reduce((s, ln) => s + calcularSubtotal(ln), 0);
  return Math.round((raw + Number.EPSILON) * 100) / 100;
}, [lineas]);


  // =========================
  // GUARDAR / ACTUALIZAR
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cabecera.proveedor_id)
      return showToast({ message: 'Selecciona un proveedor.', severity: 'warning' });
    if (!lineas.length)
      return showToast({ message: 'Agrega al menos una línea.', severity: 'warning' });

    const payload = {
      codigo: cabecera.codigo,
      proveedor_id: Number(cabecera.proveedor_id),
      estado: cabecera.estado,
      lineas: lineas.map((ln) => ({
        producto_id: Number(ln.productoObj?.id ?? ln.producto_id),
        cantidad: Number(ln.cantidad),
        precio_unitario: Number(ln.precio_unitario),
        impuesto: Number(ln.impuesto) || 0,
        descuento: Number(ln.descuento) || 0,
      })),
    };

    if (id) {
      const ok = await confirm({
        title: 'Actualizar orden',
        content: '¿Deseas actualizar esta orden?',
        confirmText: 'Actualizar',
        cancelText: 'Cancelar',
        confirmColor: 'warning',
      });
      if (!ok) return;
    }

    start();
    try {
      if (id) await updateOrdenCompra(id, payload);
      else await createOrdenCompra(payload);
      nav('/ordenes_compra', {
        replace: true,
        state: {
          flash: {
            severity: 'success',
            message: id ? 'Orden actualizada correctamente.' : 'Orden creada correctamente.',
          },
        },
      });
    } catch (err) {
      console.error(err);
      showToast({ message: 'Error al guardar la orden.', severity: 'error' });
      stop();
    }
  };

  // =========================
  // CANCELAR
  // =========================
  const handleCancel = async () => {
    if (!dirty) {
      start();
      nav('/ordenes_compra');
      return;
    }
    const ok = await confirm({
      title: 'Descartar cambios',
      content: 'Tienes cambios sin guardar. ¿Deseas salir?',
      confirmText: 'Salir sin guardar',
      cancelText: 'Seguir editando',
      confirmColor: 'warning',
    });
    if (ok) {
      start();
      nav('/ordenes_compra');
    }
  };

  // ==========
  // RENDER
  // ==========
  return (
    <Container
      maxWidth="lg"
      sx={{ mt: 2, height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}
    >
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', borderRadius: 3, boxShadow: 3, overflow: 'hidden' }}>
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            {id ? 'Editar Orden de Compra' : 'Nueva Orden de Compra'}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <form id="orden-form" onSubmit={handleSubmit}>
            {/* CABECERA */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <TextField
                label="Código"
                name="codigo"
                value={cabecera.codigo}
                onChange={handleCabeceraChange}
                required
                fullWidth
                sx={{ flex: '1 1 180px' }}
              />

              <Box sx={{ flex: '1 1 260px' }}>
                <AsyncAutocomplete
                  label="Proveedor"
                  value={proveedorSel}
                  onChange={(val) => {
                    setProveedorSel(val);
                    setCabecera((c) => ({ ...c, proveedor_id: val?.id ?? '' }));
                  }}
                  fetchPage={fetchPageProveedores}
                  limit={5}
                  placeholder="Buscar proveedor..."
                />
              </Box>

              <TextField
                select
                label="Estado"
                name="estado"
                value={cabecera.estado}
                onChange={handleCabeceraChange}
                fullWidth
                sx={{ flex: '1 1 200px' }}
              >
                <MenuItem value="pendiente">Pendiente</MenuItem>
                <MenuItem value="recibida">Recibida</MenuItem>
                <MenuItem value="cancelada">Cancelada</MenuItem>
              </TextField>
            </Box>

            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
              Detalle de Productos
            </Typography>

            <TableContainer sx={{ borderRadius: 2, boxShadow: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell align="right">Precio U.</TableCell>
                    <TableCell align="right">Impuesto (%)</TableCell>
                    <TableCell align="right">Descuento (%)</TableCell>
                    <TableCell align="right">Subtotal</TableCell>
                    <TableCell align="center">Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineas.map((ln, idx) => (
                    <TableRow key={idx}>
                      <TableCell width="25%">
                        <AsyncAutocomplete
                          label=""
                          value={ln.productoObj}
                          fetchPage={fetchPageProductos}
                          onChange={(val) => {
                            setLineas((ls) =>
                              ls.map((l, i) =>
                                i === idx ? { ...l, productoObj: val, producto_id: val?.id ?? '' } : l
                              )
                            );
                            setDirty(true);
                          }}
                        />
                      </TableCell>

                      <TableCell align="right">
                        <TextField
                          size="small"
                          name="cantidad"
                          type="number"
                          value={ln.cantidad}
                          onChange={(e) => handleLineaChange(idx, e)}
                        />
                      </TableCell>

                      <TableCell align="right">
                        <TextField
                          size="small"
                          name="precio_unitario"
                          type="number"
                          value={ln.precio_unitario}
                          onChange={(e) => handleLineaChange(idx, e)}
                        />
                      </TableCell>

                      <TableCell align="right">
                        <TextField
                          size="small"
                          name="impuesto"
                          type="number"
                          value={ln.impuesto}
                          onChange={(e) => handleLineaChange(idx, e)}
                        />
                      </TableCell>

                      <TableCell align="right">
                        <TextField
                          size="small"
                          name="descuento"
                          type="number"
                          value={ln.descuento}
                          onChange={(e) => handleLineaChange(idx, e)}
                        />
                      </TableCell>

                      <TableCell align="right">
                        ${calcularSubtotal(ln).toFixed(2)}
                      </TableCell>

                      <TableCell align="center">
                        <IconButton color="error" onClick={() => removeLinea(idx)}>
                          <RemoveCircle />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Button
                        startIcon={<AddCircle />}
                        onClick={addLinea}
                        variant="outlined"
                        sx={{ mt: 1 }}
                      >
                        Agregar Línea
                      </Button>
                    </TableCell>
                  </TableRow>

                </TableBody>
              </Table>
            </TableContainer>
          </form>
        </Box>

        {/* FOOTER */}
        <Box
          sx={{
            p: 2,
            borderTop: (t) => `1px solid ${t.palette.divider}`,
            display: 'flex',
            justifyContent: 'space-between',
            background: theme.palette.background.paper,
          }}
        >
          <Typography variant="h6">
            Total:{' '}
            {new Intl.NumberFormat('es-SV', { style: 'currency', currency: 'USD' }).format(totalOrden)}
          </Typography>

          <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={handleCancel}>Cancelar</Button>
            <Button type="submit" form="orden-form" variant="contained">
              {id ? 'Actualizar' : 'Guardar'}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Container>
  );
}
